/**
 * Lightweight signup funnel tracker. Fire-and-forget inserts into
 * public.signup_funnel_events so we can measure drop-off between:
 *   landing → /signup view → form_started → signup_completed → email_verified
 */
import { supabase } from "@/integrations/supabase/client";
import { visitorClassMetadata } from "@/lib/bot-detect";

export type FunnelEvent =
  | "landing_page_viewed"
  | "landing_cta_clicked"
  | "marketing_page_viewed"
  | "session_engaged"
  | "signup_page_viewed"
  | "signup_form_started"
  | "signup_completed"
  | "signup_failed"
  | "email_verified"
  | "start_landing_viewed"
  | "start_form_started"
  | "start_signup_failed"
  | "start_signup_completed"
  | "google_signup_autoskipped"
  | "hero_primary_cta_clicked"
  | "hero_secondary_cta_clicked"
  | "nav_signup_clicked"
  | "pricing_cta_clicked"
  | "mobile_sticky_cta_clicked"
  | "how_it_works_cta_clicked"
  | "footer_cta_clicked";


const SID_KEY = "mr_funnel_sid";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // RFC4122-ish fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getSessionId(): string {
  if (typeof window === "undefined") return uuid();
  try {
    let id = localStorage.getItem(SID_KEY);
    if (!id) { id = uuid(); localStorage.setItem(SID_KEY, id); }
    return id;
  } catch { return uuid(); }
}

const sentOnce = new Set<string>();

/**
 * Pull referrer + UTM + landing path so we can attribute landing drop-off
 * to acquisition source. Cached per session so every event in the funnel
 * can be joined back to where the visitor came from.
 */
const LANDING_CTX_KEY = "mr_landing_ctx";
function getLandingContext(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  try {
    const cached = sessionStorage.getItem(LANDING_CTX_KEY);
    if (cached) return JSON.parse(cached);
  } catch { /* ignore */ }

  const url = new URL(window.location.href);
  const params = url.searchParams;
  const ref = document.referrer || "";
  let refHost = "";
  try { refHost = ref ? new URL(ref).hostname : ""; } catch { /* ignore */ }

  // Cheap UA-based device class so the admin funnel can split conversion by
  // mobile/tablet/desktop. Not for fingerprinting — just bucketing.
  let device: "mobile" | "tablet" | "desktop" = "desktop";
  try {
    const ua = navigator.userAgent || "";
    if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) device = "tablet";
    else if (/Mobi|Android|iPhone|iPod|IEMobile|Opera Mini/i.test(ua)) device = "mobile";
  } catch { /* ignore */ }

  const ctx: Record<string, unknown> = {
    landing_path: url.pathname + url.search,
    referrer: ref || null,
    referrer_host: refHost || null,
    device,
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_term: params.get("utm_term"),       // <- the search term, when ads pass it
    utm_content: params.get("utm_content"),
    gclid: params.get("gclid"),             // Google Ads click id
    fbclid: params.get("fbclid"),           // Meta click id
    msclkid: params.get("msclkid"),         // Microsoft Ads click id
  };

  try { sessionStorage.setItem(LANDING_CTX_KEY, JSON.stringify(ctx)); } catch { /* ignore */ }
  return ctx;
}

export function trackFunnel(event: FunnelEvent, metadata: Record<string, unknown> = {}, options: { once?: boolean } = {}) {
  if (typeof window === "undefined") return;
  if (options.once) {
    const key = `${event}:${getSessionId()}`;
    if (sentOnce.has(key)) return;
    sentOnce.add(key);
  }
  const session_id = getSessionId();
  const user_id = metadata.user_id ?? null;
  // Attach landing context + bot classification to every funnel event so we
  // can attribute the full funnel back to its acquisition source and strip
  // machine traffic out of reporting.
  const merged = { ...getLandingContext(), ...visitorClassMetadata(), ...metadata };

  void supabase
    .from("signup_funnel_events" as never)
    .insert({ event, session_id, user_id, metadata: merged } as never)
    .then(({ error }) => { if (error) console.warn("[funnel]", event, error.message); });
}

/**
 * Fire `signup_completed` exactly once per user, even across:
 *  - Google OAuth redirect round-trips (same origin → localStorage persists)
 *  - page refreshes
 *  - multiple tabs / repeat SIGNED_IN events from supabase
 *  - the email-confirm callback re-entering the app
 *
 * Detection rule: a freshly-created user (created within the last 15 minutes)
 * whose user id has not yet been recorded in the localStorage dedup key.
 * Older sign-ins are silently marked as "already counted" so future fires
 * never pollute the funnel.
 */
const SIGNUP_FIRED_KEY = "mr_signup_completed_uid";
// Widened from 15min → 24h because email-confirmed signups click the
// confirmation link minutes to hours later, well outside a 15-min window.
const NEW_USER_WINDOW_MS = 24 * 60 * 60 * 1000;

export async function maybeTrackSignupCompleted(user: {
  id: string;
  created_at?: string | null;
  email_confirmed_at?: string | null;
  app_metadata?: { provider?: string } | null;
}) {
  if (typeof window === "undefined") return;
  let fired: string | null = null;
  try { fired = localStorage.getItem(SIGNUP_FIRED_KEY); } catch { /* ignore */ }
  if (fired === user.id) return;

  // Anchor freshness on whichever happened latest: account creation OR
  // first email confirmation. This handles the email-confirm round-trip
  // where SIGNED_IN doesn't fire until the user clicks the link later.
  const createdAt = user.created_at ? new Date(user.created_at).getTime() : 0;
  const confirmedAt = user.email_confirmed_at ? new Date(user.email_confirmed_at).getTime() : 0;
  const anchor = Math.max(createdAt, confirmedAt);
  const isNew = anchor > 0 && Date.now() - anchor < NEW_USER_WINDOW_MS;
  if (!isNew) {
    try { localStorage.setItem(SIGNUP_FIRED_KEY, user.id); } catch { /* ignore */ }
    return;
  }

  // Server-side dedup: if a signup_completed event already exists for this
  // user_id (different device/browser, or earlier session), don't fire again.
  try {
    const { data: existing } = await supabase
      .from("signup_funnel_events" as never)
      .select("id")
      .eq("user_id", user.id)
      .eq("event", "signup_completed")
      .limit(1);
    if (existing && existing.length > 0) {
      try { localStorage.setItem(SIGNUP_FIRED_KEY, user.id); } catch { /* ignore */ }
      return;
    }
  } catch { /* ignore — fall through and fire */ }

  try { localStorage.setItem(SIGNUP_FIRED_KEY, user.id); } catch { /* ignore */ }

  let extra: Record<string, unknown> = {};
  try {
    const raw = localStorage.getItem("mr_signup_meta");
    if (raw) { extra = JSON.parse(raw); localStorage.removeItem("mr_signup_meta"); }
  } catch { /* ignore */ }

  trackFunnel("signup_completed", {
    user_id: user.id,
    provider: user.app_metadata?.provider ?? "email",
    email_confirmed: !!user.email_confirmed_at,
    ...extra,
  }, { once: true });
}
